import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ReferenceArea, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useFetch } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const BANDS = [[0, 50, "#F43F5E", "D"], [50, 70, "#F59E0B", "C"], [70, 85, "#22D3EE", "B"], [85, 100, "#10B981", "A"]];

const Tip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-md border border-[#1F2A3E] bg-[#0B1017] px-3 py-2 text-xs">
      <div className="font-mono text-slate-400">{p.month}</div>
      <div className="text-white">{p.score == null ? "No wages" : `${p.score} · grade ${p.grade}`}{p.in_progress ? " · in progress" : ""}</div>
      {(p.penalties || []).map((x) => <div key={x.label} className="text-rose-300">−{x.points} {x.label}</div>)}
    </div>);
};

export const Change = ({ d }) => {
  if (d.change == null) return <span className="text-xs text-slate-500" data-testid="trend-change">Not enough scored months to compare yet</span>;
  const I = d.change > 0 ? TrendingUp : d.change < 0 ? TrendingDown : Minus;
  const tone = d.change > 0 ? "text-emerald-400" : d.change < 0 ? "text-rose-400" : "text-slate-300";
  return (
    <span className={`flex items-center gap-1.5 text-sm ${tone}`} data-testid="trend-change">
      <I size={15} />{d.change > 0 ? "+" : ""}{d.change} points
      <span className="text-xs text-slate-500">({d.first.month} {d.first.score} → {d.last.month} {d.last.score})</span>
    </span>);
};

const PenaltyTable = ({ trend }) => {
  const rows = trend.filter((t) => t.penalty);
  if (!rows.length) return <p className="text-xs text-emerald-300 mt-3" data-testid="trend-no-penalties">No penalties in the last 12 months.</p>;
  return (
    <table className="w-full text-xs mt-3" data-testid="trend-penalty-table">
      <thead><tr className="text-left text-slate-500"><th className="py-1.5">Month</th><th>Before penalties</th><th>Penalties</th><th className="text-right">Score</th></tr></thead>
      <tbody>{rows.map((t) => (
        <tr key={t.month} className="border-t border-[#1F2A3E]" data-testid={`trend-penalty-${t.month}`}>
          <td className="py-1.5 font-mono">{t.month}</td><td className="font-mono">{t.base_score}</td>
          <td className="text-rose-300">{t.penalties.map((p) => `−${p.points} ${p.label}`).join(" · ")}</td>
          <td className="text-right font-mono">{t.score} ({t.grade})</td></tr>))}</tbody></table>);
};

export const ScoreTrendDialog = ({ eid, vendor, month, onClose }) => {
  const { data } = useFetch(`/pe/${eid}/scorecard/${vendor.vendor_id}/trend?month=${month}`);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="!bg-[#121824] border-[#1F2A3E] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="score-trend-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{vendor.vendor_name} · 12-month score</DialogTitle></DialogHeader>
        {!data ? <p className="text-xs text-slate-500 font-mono">Loading…</p> : <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Change d={data} />
            <span className="text-xs text-slate-400" data-testid="trend-average">12-month average <span className="font-mono text-white">{data.average_12m ?? "—"}</span></span>
          </div>
          <TrendChart trend={data.trend} penalties />
          <PenaltyTable trend={data.trend} />
        </>}
      </DialogContent>
    </Dialog>);
};

export const TrendChart = ({ trend, penalties = false }) => (
  <>
          <div className="h-72" data-testid="score-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trend} margin={{ top: 10, right: 16, bottom: 0, left: -16 }}>
                {BANDS.map(([y1, y2, c, g]) => <ReferenceArea key={g} y1={y1} y2={y2} fill={c} fillOpacity={0.08} stroke="none" ifOverflow="hidden"
                  label={{ value: g, position: "insideRight", fill: c, fontSize: 11 }} />)}
                <CartesianGrid stroke="#1F2A3E" vertical={false} />
                <XAxis dataKey="month" tickFormatter={(m) => m.slice(2)} stroke="#64748B" fontSize={11} />
                <YAxis domain={[0, 100]} ticks={[0, 50, 70, 85, 100]} stroke="#64748B" fontSize={11} />
                <Tooltip content={<Tip />} />
                {penalties && <Bar dataKey="penalty" fill="#F43F5E" fillOpacity={0.75} barSize={14} radius={[3, 3, 0, 0]} />}
                {penalties && <Line type="monotone" dataKey="base_score" stroke="#64748B" strokeDasharray="4 4" strokeWidth={1.5} connectNulls={false}
                  dot={(p) => <circle key={`b${p.index}`} cx={p.cx} cy={p.cy} r={p.payload.penalty ? 3.5 : 0} fill="#121824" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="2 2" />} />}
                <Line type="monotone" dataKey="score" stroke="#00E599" strokeWidth={2} connectNulls={false}
                  dot={(p) => <circle key={p.index} cx={p.cx} cy={p.cy} r={p.payload.score == null ? 0 : 4} fill={p.payload.in_progress ? "#121824" : "#00E599"} stroke="#00E599" strokeWidth={2} />} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-slate-500">Shaded bands show grades A (85+), B (70+), C (50+) and D. Hollow dots are months not yet due; gaps are months with no wages.
            {penalties && " Red bars are penalty points taken off that month; the grey dashed line and dashed dots show the score before penalties."}</p>
  </>);
